import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';

/* ═══════════════════════════════════════
   BoardEditor — 글 쓰기 / 수정 (v2 Upgraded)
   ═══════════════════════════════════════ */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const tooltipFadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Wrapper ──
const EditorWrapper = styled.div`
  background: ${({ theme }) => theme.colors.neutral[0]};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  box-shadow: ${({ theme }) => theme.elevation.md};
  overflow: hidden;
  margin-bottom: 24px;
  animation: ${fadeIn} 0.4s ease both;
  position: relative;

  ${({ $fullscreen }) =>
    $fullscreen &&
    css`
      position: fixed;
      inset: 0;
      z-index: 9999;
      border-radius: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      overflow: auto;
    `}
`;

// ── Header ──
const EditorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 28px;
  background: ${({ theme }) => theme.colors.neutral[50]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};

  @media (max-width: 640px) {
    padding: 14px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const EditorTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.typography.fontFamily.semibold};
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral[800]};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const HeaderToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid ${({ theme, $active }) =>
    $active ? theme.colors.primary[400] : theme.colors.neutral[200]};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary[50] : theme.colors.neutral[0]};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary[700] : theme.colors.neutral[600]};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primary[50]};
    border-color: ${({ theme }) => theme.colors.primary[300]};
    color: ${({ theme }) => theme.colors.primary[700]};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// ── Form Fields ──
const FieldGroup = styled.div`
  padding: 20px 28px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const FieldRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: ${({ $mb }) => $mb || '0'};

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const FieldFlex = styled.div`
  flex: 1;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral[500]};
  letter-spacing: 0.3px;
`;

const TitleInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  font-size: 1.05rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutral[800]};
  font-family: inherit;
  transition: border-color ${({ theme }) => theme.transition.fast},
              box-shadow ${({ theme }) => theme.transition.fast};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary[400]};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary[50]};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.neutral[400]};
  }
`;

const AuthorInput = styled(TitleInput)`
  font-size: 0.92rem;
  font-weight: 400;
  flex: 1;
`;

// ── Toolbar ──
const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 8px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  background: ${({ theme }) => theme.colors.neutral[50]};

  @media (max-width: 640px) {
    padding: 6px 12px;
  }
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
  background: ${({ theme }) => theme.colors.neutral[0]};
  border: 1px solid ${({ theme }) => theme.colors.neutral[150] || theme.colors.neutral[100]};
  border-radius: 8px;
  padding: 2px;
  margin-right: 6px;
  margin-bottom: 2px;
`;

const ToolbarBtn = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 4px;
  border: none;
  border-radius: 6px;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary[100] : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary[700] : theme.colors.neutral[600]};
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 600;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.primary[50]};
    color: ${({ theme }) => theme.colors.primary[700]};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    background: ${({ theme }) => theme.colors.primary[100]};
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

const ToolbarBtnWide = styled(ToolbarBtn)`
  padding: 0 8px;
  font-size: 0.72rem;
  gap: 3px;
`;

// ── Color Palette Dropdown ──
const DropdownWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const DropdownPanel = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: ${({ theme }) => theme.colors.neutral[0]};
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 10px;
  box-shadow: ${({ theme }) => theme.elevation.lg};
  padding: 8px;
  z-index: 100;
  animation: ${tooltipFadeIn} 0.15s ease;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 120px;
`;

const ColorSwatch = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid ${({ $selected, theme }) =>
    $selected ? theme.colors.primary[500] : theme.colors.neutral[200]};
  background: ${({ $color }) => $color};
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;

  &:hover {
    transform: scale(1.2);
    border-color: ${({ theme }) => theme.colors.primary[400]};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

// ── Font Size Dropdown ──
const FontSizeOption = styled.button`
  display: block;
  width: 100%;
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary[50] : 'transparent'};
  color: ${({ theme }) => theme.colors.neutral[700]};
  font-size: ${({ $fontSize }) => $fontSize};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;
  text-align: left;
  transition: background 0.1s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.primary[50]};
  }
`;

const FontSizePanel = styled(DropdownPanel)`
  flex-direction: column;
  gap: 2px;
  min-width: 130px;
  padding: 6px;
`;

// ── Table Panel ──
const TablePanel = styled(DropdownPanel)`
  flex-direction: column;
  gap: 4px;
  min-width: 140px;
  padding: 10px;
`;

const TableOption = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.neutral[700]};
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.1s ease;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.primary[50]};
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.neutral[500]};
  }
`;

// ── Image Dialog ──
const ImageDialogTabs = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 16px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.neutral[100]};
`;

const ImageTab = styled.button`
  flex: 1;
  padding: 10px;
  border: none;
  background: transparent;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary[600] : theme.colors.neutral[500]};
  font-size: 0.85rem;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  cursor: pointer;
  border-bottom: 2px solid ${({ theme, $active }) =>
    $active ? theme.colors.primary[500] : 'transparent'};
  margin-bottom: -2px;
  transition: all 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary[600]};
  }
`;

const UploadArea = styled.div`
  border: 2px dashed ${({ theme, $isDragging }) =>
    $isDragging ? theme.colors.primary[400] : theme.colors.neutral[200]};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  padding: 32px 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ theme, $isDragging }) =>
    $isDragging ? theme.colors.primary[50] : theme.colors.neutral[50]};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary[300]};
    background: ${({ theme }) => theme.colors.primary[50]};
  }
`;

const UploadIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 8px;
`;

const UploadText = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.neutral[500]};
`;

const UploadSubText = styled.p`
  margin: 4px 0 0;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.neutral[400]};
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadProgress = styled.div`
  margin-top: 12px;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.primary[600]};
  font-weight: 600;
`;

// ── Content Editor (contentEditable) ──
const EditorArea = styled.div`
  padding: 20px 28px;
  min-height: 320px;

  ${({ $fullscreen }) =>
    $fullscreen &&
    css`
      flex: 1;
      overflow-y: auto;
    `}

  @media (max-width: 640px) {
    padding: 16px;
    min-height: 240px;
  }
`;

const ContentEditable = styled.div`
  min-height: 280px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  font-size: 0.95rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.neutral[800]};
  outline: none;
  transition: border-color ${({ theme }) => theme.transition.fast},
              box-shadow ${({ theme }) => theme.transition.fast};
  overflow-y: auto;
  word-break: break-word;

  ${({ $fullscreen }) =>
    $fullscreen &&
    css`
      min-height: unset;
      height: 100%;
    `}

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary[400]};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary[50]};
  }

  &:empty::before {
    content: attr(data-placeholder);
    color: ${({ theme }) => theme.colors.neutral[400]};
    pointer-events: none;
  }

  /* Rich text styles */
  b, strong { font-weight: 700; }
  i, em { font-style: italic; }
  u { text-decoration: underline; }

  a {
    color: ${({ theme }) => theme.colors.primary[600]};
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  code {
    padding: 2px 6px;
    background: ${({ theme }) => theme.colors.neutral[100]};
    border-radius: 4px;
    font-family: ${({ theme }) => theme.typography.fontFamily.mono};
    font-size: 0.88em;
    color: ${({ theme }) => theme.colors.semantic.error};
  }

  pre {
    background: ${({ theme }) => theme.colors.neutral[900]};
    color: #e5e7eb;
    padding: 16px;
    border-radius: ${({ theme }) => theme.borderRadius.base};
    overflow-x: auto;
    margin: 12px 0;
    font-family: ${({ theme }) => theme.typography.fontFamily.mono};
    font-size: 0.88rem;
    line-height: 1.6;

    code {
      background: none;
      color: inherit;
      padding: 0;
    }
  }

  blockquote {
    border-left: 4px solid ${({ theme }) => theme.colors.primary[300]};
    margin: 12px 0;
    padding: 8px 16px;
    background: ${({ theme }) => theme.colors.primary[50]};
    border-radius: 0 8px 8px 0;
    color: ${({ theme }) => theme.colors.neutral[700]};
  }

  h1, h2, h3 {
    font-family: ${({ theme }) => theme.typography.fontFamily.semibold};
    color: ${({ theme }) => theme.colors.neutral[900]};
    margin: 16px 0 8px;
  }

  h1 { font-size: 1.5rem; }
  h2 { font-size: 1.25rem; }
  h3 { font-size: 1.1rem; }

  ul, ol {
    padding-left: 24px;
    margin: 8px 0;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 8px 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
    border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
    border-radius: 8px;
    overflow: hidden;
  }

  th, td {
    border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
    padding: 10px 14px;
    text-align: left;
    min-width: 60px;
  }

  th {
    background: ${({ theme }) => theme.colors.neutral[50]};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.neutral[700]};
  }

  hr {
    border: none;
    height: 2px;
    background: linear-gradient(to right,
      transparent,
      ${({ theme }) => theme.colors.neutral[300]},
      transparent
    );
    margin: 20px 0;
  }

  @media (max-width: 640px) {
    min-height: 200px;
    padding: 12px;
    font-size: 0.9rem;
  }
`;

// ── Preview ──
const PreviewArea = styled.div`
  min-height: 280px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  font-size: 0.95rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.neutral[800]};
  background: ${({ theme }) => theme.colors.neutral[50]};
  overflow-y: auto;
  word-break: break-word;

  b, strong { font-weight: 700; }
  i, em { font-style: italic; }
  u { text-decoration: underline; }

  a {
    color: ${({ theme }) => theme.colors.primary[600]};
    text-decoration: underline;
  }

  code {
    padding: 2px 6px;
    background: ${({ theme }) => theme.colors.neutral[100]};
    border-radius: 4px;
    font-family: ${({ theme }) => theme.typography.fontFamily.mono};
    font-size: 0.88em;
    color: ${({ theme }) => theme.colors.semantic.error};
  }

  pre {
    background: ${({ theme }) => theme.colors.neutral[900]};
    color: #e5e7eb;
    padding: 16px;
    border-radius: ${({ theme }) => theme.borderRadius.base};
    overflow-x: auto;
    margin: 12px 0;
    font-family: ${({ theme }) => theme.typography.fontFamily.mono};
    font-size: 0.88rem;
    line-height: 1.6;
    code { background: none; color: inherit; padding: 0; }
  }

  blockquote {
    border-left: 4px solid ${({ theme }) => theme.colors.primary[300]};
    margin: 12px 0;
    padding: 8px 16px;
    background: ${({ theme }) => theme.colors.primary[50]};
    border-radius: 0 8px 8px 0;
  }

  h1, h2, h3 {
    font-family: ${({ theme }) => theme.typography.fontFamily.semibold};
    color: ${({ theme }) => theme.colors.neutral[900]};
    margin: 16px 0 8px;
  }
  h1 { font-size: 1.5rem; }
  h2 { font-size: 1.25rem; }
  h3 { font-size: 1.1rem; }

  ul, ol { padding-left: 24px; margin: 8px 0; }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 8px 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
    border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  }
  th, td {
    border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
    padding: 10px 14px;
    text-align: left;
  }
  th {
    background: ${({ theme }) => theme.colors.neutral[100]};
    font-weight: 600;
  }

  hr {
    border: none;
    height: 2px;
    background: linear-gradient(to right, transparent,
      ${({ theme }) => theme.colors.neutral[300]}, transparent);
    margin: 20px 0;
  }
`;

const PreviewBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  margin-bottom: 12px;
  background: ${({ theme }) => theme.colors.primary[50]};
  color: ${({ theme }) => theme.colors.primary[600]};
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.3px;
`;

// ── Footer ──
const EditorFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 28px;
  border-top: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  background: ${({ theme }) => theme.colors.neutral[50]};

  @media (max-width: 640px) {
    padding: 14px 16px;
    flex-direction: column-reverse;
    gap: 10px;
  }
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const FooterInfo = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.neutral[400]};
`;

const CharCounter = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral[500]};
  background: ${({ theme }) => theme.colors.neutral[100]};
  padding: 3px 10px;
  border-radius: 12px;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const CancelBtn = styled.button`
  padding: 10px 24px;
  font-size: 0.9rem;
  font-weight: 600;
  background: ${({ theme }) => theme.colors.neutral[0]};
  color: ${({ theme }) => theme.colors.neutral[600]};
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.neutral[50]};
    border-color: ${({ theme }) => theme.colors.neutral[300]};
  }

  @media (max-width: 640px) {
    flex: 1;
  }
`;

const SubmitBtn = styled.button`
  padding: 10px 28px;
  font-size: 0.9rem;
  font-weight: 700;
  background: ${({ theme }) => theme.colors.primary[500]};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.base};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.elevation.md};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 640px) {
    flex: 1;
  }
`;

// ── Dialogs (shared overlay) ──
const DialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: ${fadeIn} 0.2s ease;
`;

const DialogBox = styled.div`
  background: ${({ theme }) => theme.colors.neutral[0]};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.elevation.xl};
  padding: 24px;
  width: 90%;
  max-width: 420px;
`;

const DialogTitle = styled.h4`
  margin: 0 0 16px;
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral[800]};
`;

const DialogInput = styled(TitleInput)`
  font-size: 0.9rem;
  margin-bottom: 12px;
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

// ── SVG Icons ──
const IconBold = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
    <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
  </svg>
);

const IconItalic = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="4" x2="10" y2="4" />
    <line x1="14" y1="20" x2="5" y2="20" />
    <line x1="15" y1="4" x2="9" y2="20" />
  </svg>
);

const IconUnderline = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" />
    <line x1="4" y1="21" x2="20" y2="21" />
  </svg>
);

const IconStrikethrough = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4H9a3 3 0 0 0 0 6h6" />
    <path d="M8 20h7a3 3 0 0 0 0-6H4" />
    <line x1="4" y1="12" x2="20" y2="12" />
  </svg>
);

const IconUL = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <circle cx="4" cy="6" r="1" fill="currentColor" />
    <circle cx="4" cy="12" r="1" fill="currentColor" />
    <circle cx="4" cy="18" r="1" fill="currentColor" />
  </svg>
);

const IconOL = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="10" y1="6" x2="21" y2="6" />
    <line x1="10" y1="12" x2="21" y2="12" />
    <line x1="10" y1="18" x2="21" y2="18" />
    <text x="2" y="8" fontSize="8" fill="currentColor" stroke="none">1</text>
    <text x="2" y="14" fontSize="8" fill="currentColor" stroke="none">2</text>
    <text x="2" y="20" fontSize="8" fill="currentColor" stroke="none">3</text>
  </svg>
);

const IconQuote = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
  </svg>
);

const IconCode = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const IconLink = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const IconImage = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const IconTable = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="3" y1="15" x2="21" y2="15" />
    <line x1="9" y1="3" x2="9" y2="21" />
    <line x1="15" y1="3" x2="15" y2="21" />
  </svg>
);

const IconHR = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" y1="12" x2="21" y2="12" />
    <circle cx="7" cy="12" r="1" fill="currentColor" />
    <circle cx="12" cy="12" r="1" fill="currentColor" />
    <circle cx="17" cy="12" r="1" fill="currentColor" />
  </svg>
);

const IconUndo = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
  </svg>
);

const IconRedo = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" />
  </svg>
);

const IconPreview = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconFullscreen = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const IconExitFullscreen = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="4 14 10 14 10 20" />
    <polyline points="20 10 14 10 14 4" />
    <line x1="14" y1="10" x2="21" y2="3" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const IconFontSize = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <text x="2" y="18" fontSize="16" fill="currentColor" stroke="none" fontWeight="bold">A</text>
    <text x="14" y="18" fontSize="11" fill="currentColor" stroke="none" fontWeight="bold">A</text>
  </svg>
);

const IconColor = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3L4 21h2.5l2-5h7l2 5H20L12 3z" />
    <line x1="8" y1="13" x2="16" y2="13" />
  </svg>
);

// ── Color definitions ──
const COLOR_PALETTE = [
  { name: '검정', value: '#000000' },
  { name: '빨강', value: '#e53e3e' },
  { name: '파랑', value: '#3182ce' },
  { name: '초록', value: '#38a169' },
  { name: '주황', value: '#dd6b20' },
  { name: '보라', value: '#805ad5' },
];

// ── Font size definitions ──
const FONT_SIZES = [
  { label: '작은 글자', value: '1', css: '0.8rem' },
  { label: '보통', value: '3', css: '1rem' },
  { label: '큰 글자', value: '5', css: '1.25rem' },
  { label: '아주 큰 글자', value: '7', css: '1.5rem' },
];

// ── Component ──
function BoardEditor({
  editData = null,
  session,
  onSubmit,
  onCancel,
}) {
  const isEditMode = !!editData;
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  const [title, setTitle] = useState(editData?.NOTICE_TITLE || editData?.notice_title || '');
  const [authorName, setAuthorName] = useState('');
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');

  // New states
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [imageTab, setImageTab] = useState('upload'); // 'upload' | 'url'
  const [imageUrl, setImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [showColorPalette, setShowColorPalette] = useState(false);
  const [currentColor, setCurrentColor] = useState('#000000');
  const [showFontSize, setShowFontSize] = useState(false);
  const [showTableMenu, setShowTableMenu] = useState(false);

  const [isPreview, setIsPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [charCount, setCharCount] = useState(0);

  // Save selection for restoring after dialog
  const savedSelectionRef = useRef(null);

  useEffect(() => {
    if (session?.Name) {
      setAuthorName(session.Name);
    }
  }, [session]);

  useEffect(() => {
    if (isEditMode && editorRef.current) {
      const content = editData?.NOTICE_CONTENTS || editData?.notice_contents || '';
      editorRef.current.innerHTML = content;
      updateCharCount();
    }
  }, [editData, isEditMode, updateCharCount]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowColorPalette(false);
      setShowFontSize(false);
      setShowTableMenu(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // ESC to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // ── Helpers ──
  const updateCharCount = useCallback(() => {
    if (editorRef.current) {
      const text = editorRef.current.innerText || '';
      setCharCount(text.replace(/\n/g, '').length);
    }
  }, []);

  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (savedSelectionRef.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }
  }, []);

  // ── Toolbar Commands ──
  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    updateCharCount();
  }, [updateCharCount]);

  const handleBold = useCallback(() => execCommand('bold'), [execCommand]);
  const handleItalic = useCallback(() => execCommand('italic'), [execCommand]);
  const handleUnderline = useCallback(() => execCommand('underline'), [execCommand]);
  const handleStrikethrough = useCallback(() => execCommand('strikeThrough'), [execCommand]);
  const handleUL = useCallback(() => execCommand('insertUnorderedList'), [execCommand]);
  const handleOL = useCallback(() => execCommand('insertOrderedList'), [execCommand]);
  const handleBlockquote = useCallback(() => execCommand('formatBlock', 'blockquote'), [execCommand]);
  const handleH2 = useCallback(() => execCommand('formatBlock', 'h2'), [execCommand]);
  const handleH3 = useCallback(() => execCommand('formatBlock', 'h3'), [execCommand]);
  const handleParagraph = useCallback(() => execCommand('formatBlock', 'p'), [execCommand]);

  // Undo / Redo
  const handleUndo = useCallback(() => execCommand('undo'), [execCommand]);
  const handleRedo = useCallback(() => execCommand('redo'), [execCommand]);

  // HR
  const handleHR = useCallback(() => {
    execCommand('insertHorizontalRule');
  }, [execCommand]);

  // Font size
  const handleFontSize = useCallback((size) => {
    execCommand('fontSize', size);
    setShowFontSize(false);
  }, [execCommand]);

  // Color
  const handleColor = useCallback((color) => {
    execCommand('foreColor', color);
    setCurrentColor(color);
    setShowColorPalette(false);
  }, [execCommand]);

  // Table
  const handleInsertTable = useCallback((rows, cols) => {
    const headerCells = Array.from({ length: cols }, (_, i) => `<th>제목 ${i + 1}</th>`).join('');
    const bodyRows = Array.from({ length: rows - 1 }, () => {
      const cells = Array.from({ length: cols }, () => '<td>내용</td>').join('');
      return `<tr>${cells}</tr>`;
    }).join('');
    const html = `<table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table><p><br></p>`;
    execCommand('insertHTML', html);
    setShowTableMenu(false);
  }, [execCommand]);

  const handleCode = useCallback(() => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const selectedText = range.toString();
      if (selectedText) {
        const code = document.createElement('code');
        code.textContent = selectedText;
        range.deleteContents();
        range.insertNode(code);
      }
    }
    editorRef.current?.focus();
  }, []);

  const handleCodeBlock = useCallback(() => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const selectedText = range.toString() || '// 코드를 입력하세요';
      const pre = document.createElement('pre');
      const code = document.createElement('code');
      code.textContent = selectedText;
      pre.appendChild(code);
      range.deleteContents();
      range.insertNode(pre);
      const newRange = document.createRange();
      newRange.setStartAfter(pre);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }
    editorRef.current?.focus();
  }, []);

  // ── Link Dialog ──
  const handleLink = useCallback(() => {
    const selection = window.getSelection();
    const text = selection?.toString() || '';
    setLinkText(text);
    setLinkUrl('');
    saveSelection();
    setShowLinkDialog(true);
  }, [saveSelection]);

  const handleInsertLink = useCallback(() => {
    if (!linkUrl) return;
    const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
    restoreSelection();
    if (linkText) {
      const a = document.createElement('a');
      a.href = url;
      a.textContent = linkText;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(a);
      }
    } else {
      execCommand('createLink', url);
    }
    setShowLinkDialog(false);
    editorRef.current?.focus();
  }, [linkUrl, linkText, execCommand, restoreSelection]);

  // ── Image Dialog ──
  const handleOpenImageDialog = useCallback(() => {
    saveSelection();
    setImageUrl('');
    setImageTab('upload');
    setShowImageDialog(true);
  }, [saveSelection]);

  const handleInsertImageUrl = useCallback(() => {
    if (!imageUrl) return;
    restoreSelection();
    const html = `<img src="${imageUrl}" alt="image" /><p><br></p>`;
    document.execCommand('insertHTML', false, html);
    setShowImageDialog(false);
    editorRef.current?.focus();
    updateCharCount();
  }, [imageUrl, restoreSelection, updateCharCount]);

  const handleFileUpload = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드할 수 있습니다.');
      return;
    }
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/request/FileUpload', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data && (data.url || data.filePath || data.path)) {
        const imgUrl = data.url || data.filePath || data.path;
        restoreSelection();
        const html = `<img src="${imgUrl}" alt="${file.name}" /><p><br></p>`;
        document.execCommand('insertHTML', false, html);
        setShowImageDialog(false);
        editorRef.current?.focus();
        updateCharCount();
      } else {
        alert('이미지 업로드에 실패했습니다.');
      }
    } catch (err) {
      console.error('Image upload error:', err);
      alert('이미지 업로드 중 오류가 발생했습니다.');
    } finally {
      setIsUploading(false);
    }
  }, [restoreSelection, updateCharCount]);

  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  // ── Preview ──
  const togglePreview = useCallback(() => {
    if (!isPreview && editorRef.current) {
      setPreviewHtml(editorRef.current.innerHTML);
    }
    setIsPreview((prev) => !prev);
  }, [isPreview]);

  // ── Fullscreen ──
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  // ── Content input handler ──
  const handleInput = useCallback(() => {
    updateCharCount();
  }, [updateCharCount]);

  // ── Submit (UNTOUCHED) ──
  const handleSubmit = useCallback(() => {
    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    const contents = editorRef.current?.innerHTML || '';
    if (!contents.trim() || contents === '<br>') {
      alert('내용을 입력해주세요.');
      return;
    }

    const data = {
      title: title.trim(),
      contents,
      name: authorName || session?.Name || '',
    };

    if (isEditMode) {
      data.NUM = editData?.NUM || editData?.num;
    }

    onSubmit?.(data);
  }, [title, authorName, session, isEditMode, editData, onSubmit]);

  return (
    <EditorWrapper $fullscreen={isFullscreen}>
      {/* Header */}
      <EditorHeader>
        <EditorTitle>
          {isEditMode ? '📝 글 수정' : '✍️ 새 글 작성'}
        </EditorTitle>
        <HeaderActions>
          <HeaderToggleBtn
            $active={isPreview}
            onClick={togglePreview}
            title="미리보기"
          >
            <IconPreview /> {isPreview ? '편집' : '미리보기'}
          </HeaderToggleBtn>
          <HeaderToggleBtn
            $active={isFullscreen}
            onClick={toggleFullscreen}
            title={isFullscreen ? '전체화면 해제' : '전체화면'}
          >
            {isFullscreen ? <IconExitFullscreen /> : <IconFullscreen />}
            {isFullscreen ? '축소' : '전체화면'}
          </HeaderToggleBtn>
        </HeaderActions>
      </EditorHeader>

      {/* Fields */}
      <FieldGroup>
        <FieldRow $mb="12px">
          <FieldFlex>
            <FieldLabel>작성자</FieldLabel>
            <AuthorInput
              type="text"
              placeholder="이름"
              value={session?.Name || authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              readOnly={!!session?.UserId}
            />
          </FieldFlex>
        </FieldRow>
        <div>
          <FieldLabel>제목</FieldLabel>
          <TitleInput
            type="text"
            placeholder="제목을 입력하세요"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
        </div>
      </FieldGroup>

      {/* Toolbar */}
      {!isPreview && (
        <Toolbar>
          {/* Undo / Redo */}
          <ToolbarGroup>
            <ToolbarBtn title="되돌리기 (Ctrl+Z)" onClick={handleUndo}><IconUndo /></ToolbarBtn>
            <ToolbarBtn title="다시실행 (Ctrl+Y)" onClick={handleRedo}><IconRedo /></ToolbarBtn>
          </ToolbarGroup>

          {/* Block Format */}
          <ToolbarGroup>
            <ToolbarBtnWide title="본문 (P)" onClick={handleParagraph}>P</ToolbarBtnWide>
            <ToolbarBtnWide title="제목2 (H2)" onClick={handleH2}>H2</ToolbarBtnWide>
            <ToolbarBtnWide title="제목3 (H3)" onClick={handleH3}>H3</ToolbarBtnWide>
          </ToolbarGroup>

          {/* Inline Format */}
          <ToolbarGroup>
            <ToolbarBtn title="굵게 (Bold)" onClick={handleBold}><IconBold /></ToolbarBtn>
            <ToolbarBtn title="기울임 (Italic)" onClick={handleItalic}><IconItalic /></ToolbarBtn>
            <ToolbarBtn title="밑줄 (Underline)" onClick={handleUnderline}><IconUnderline /></ToolbarBtn>
            <ToolbarBtn title="취소선" onClick={handleStrikethrough}><IconStrikethrough /></ToolbarBtn>
          </ToolbarGroup>

          {/* Font Size */}
          <ToolbarGroup>
            <DropdownWrapper onClick={(e) => e.stopPropagation()}>
              <ToolbarBtn
                title="글자 크기"
                onClick={() => {
                  setShowFontSize((v) => !v);
                  setShowColorPalette(false);
                  setShowTableMenu(false);
                }}
              >
                <IconFontSize />
              </ToolbarBtn>
              {showFontSize && (
                <FontSizePanel>
                  {FONT_SIZES.map((s) => (
                    <FontSizeOption
                      key={s.value}
                      $fontSize={s.css}
                      onClick={() => handleFontSize(s.value)}
                    >
                      {s.label}
                    </FontSizeOption>
                  ))}
                </FontSizePanel>
              )}
            </DropdownWrapper>
          </ToolbarGroup>

          {/* Color */}
          <ToolbarGroup>
            <DropdownWrapper onClick={(e) => e.stopPropagation()}>
              <ToolbarBtn
                title="글자 색상"
                onClick={() => {
                  setShowColorPalette((v) => !v);
                  setShowFontSize(false);
                  setShowTableMenu(false);
                }}
              >
                <IconColor />
              </ToolbarBtn>
              {showColorPalette && (
                <DropdownPanel>
                  {COLOR_PALETTE.map((c) => (
                    <ColorSwatch
                      key={c.value}
                      $color={c.value}
                      $selected={currentColor === c.value}
                      title={c.name}
                      onClick={() => handleColor(c.value)}
                    />
                  ))}
                </DropdownPanel>
              )}
            </DropdownWrapper>
          </ToolbarGroup>

          {/* Lists & Quote */}
          <ToolbarGroup>
            <ToolbarBtn title="번호 없는 목록" onClick={handleUL}><IconUL /></ToolbarBtn>
            <ToolbarBtn title="번호 목록" onClick={handleOL}><IconOL /></ToolbarBtn>
            <ToolbarBtn title="인용문" onClick={handleBlockquote}><IconQuote /></ToolbarBtn>
          </ToolbarGroup>

          {/* Code */}
          <ToolbarGroup>
            <ToolbarBtnWide title="인라인 코드" onClick={handleCode}>{'</>'}</ToolbarBtnWide>
            <ToolbarBtn title="코드 블록" onClick={handleCodeBlock}><IconCode /></ToolbarBtn>
          </ToolbarGroup>

          {/* Insert */}
          <ToolbarGroup>
            <ToolbarBtn title="링크 삽입" onClick={handleLink}><IconLink /></ToolbarBtn>
            <ToolbarBtn title="이미지 삽입" onClick={handleOpenImageDialog}><IconImage /></ToolbarBtn>
            <DropdownWrapper onClick={(e) => e.stopPropagation()}>
              <ToolbarBtn
                title="표 삽입"
                onClick={() => {
                  setShowTableMenu((v) => !v);
                  setShowColorPalette(false);
                  setShowFontSize(false);
                }}
              >
                <IconTable />
              </ToolbarBtn>
              {showTableMenu && (
                <TablePanel>
                  <TableOption onClick={() => handleInsertTable(2, 2)}>
                    <IconTable /> 2×2 표
                  </TableOption>
                  <TableOption onClick={() => handleInsertTable(3, 3)}>
                    <IconTable /> 3×3 표
                  </TableOption>
                  <TableOption onClick={() => handleInsertTable(4, 4)}>
                    <IconTable /> 4×4 표
                  </TableOption>
                </TablePanel>
              )}
            </DropdownWrapper>
            <ToolbarBtn title="구분선 삽입" onClick={handleHR}><IconHR /></ToolbarBtn>
          </ToolbarGroup>
        </Toolbar>
      )}

      {/* Editor / Preview Body */}
      <EditorArea $fullscreen={isFullscreen}>
        {isPreview ? (
          <>
            <PreviewBadge>👁 미리보기 모드</PreviewBadge>
            <PreviewArea dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </>
        ) : (
          <ContentEditable
            ref={editorRef}
            contentEditable
            data-placeholder="본문을 입력하세요..."
            suppressContentEditableWarning
            onInput={handleInput}
            $fullscreen={isFullscreen}
          />
        )}
      </EditorArea>

      {/* Footer */}
      <EditorFooter>
        <FooterLeft>
          <FooterInfo>
            {isEditMode ? '수정 모드' : '새 글 작성 모드'}
          </FooterInfo>
          <CharCounter>📝 {charCount.toLocaleString()}자</CharCounter>
        </FooterLeft>
        <FooterActions>
          <CancelBtn onClick={onCancel}>취소</CancelBtn>
          <SubmitBtn onClick={handleSubmit}>
            {isEditMode ? '수정 완료' : '작성 완료'}
          </SubmitBtn>
        </FooterActions>
      </EditorFooter>

      {/* Link Dialog */}
      {showLinkDialog && (
        <DialogOverlay onClick={() => setShowLinkDialog(false)}>
          <DialogBox onClick={(e) => e.stopPropagation()}>
            <DialogTitle>🔗 링크 삽입</DialogTitle>
            <FieldLabel>표시 텍스트</FieldLabel>
            <DialogInput
              type="text"
              placeholder="링크 텍스트"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
            />
            <FieldLabel>URL</FieldLabel>
            <DialogInput
              type="url"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleInsertLink();
              }}
            />
            <DialogActions>
              <CancelBtn onClick={() => setShowLinkDialog(false)}>취소</CancelBtn>
              <SubmitBtn onClick={handleInsertLink} disabled={!linkUrl}>삽입</SubmitBtn>
            </DialogActions>
          </DialogBox>
        </DialogOverlay>
      )}

      {/* Image Dialog */}
      {showImageDialog && (
        <DialogOverlay onClick={() => setShowImageDialog(false)}>
          <DialogBox onClick={(e) => e.stopPropagation()}>
            <DialogTitle>🖼️ 이미지 삽입</DialogTitle>
            <ImageDialogTabs>
              <ImageTab
                $active={imageTab === 'upload'}
                onClick={() => setImageTab('upload')}
              >
                📁 파일 업로드
              </ImageTab>
              <ImageTab
                $active={imageTab === 'url'}
                onClick={() => setImageTab('url')}
              >
                🔗 URL 입력
              </ImageTab>
            </ImageDialogTabs>

            {imageTab === 'upload' ? (
              <>
                <UploadArea
                  $isDragging={isDragging}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <UploadIcon>📷</UploadIcon>
                  <UploadText>클릭하거나 파일을 드래그하세요</UploadText>
                  <UploadSubText>JPG, PNG, GIF, WebP (최대 10MB)</UploadSubText>
                  {isUploading && <UploadProgress>⏳ 업로드 중...</UploadProgress>}
                </UploadArea>
                <HiddenFileInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                />
              </>
            ) : (
              <>
                <FieldLabel>이미지 URL</FieldLabel>
                <DialogInput
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleInsertImageUrl();
                  }}
                />
                <DialogActions>
                  <CancelBtn onClick={() => setShowImageDialog(false)}>취소</CancelBtn>
                  <SubmitBtn onClick={handleInsertImageUrl} disabled={!imageUrl}>삽입</SubmitBtn>
                </DialogActions>
              </>
            )}
          </DialogBox>
        </DialogOverlay>
      )}
    </EditorWrapper>
  );
}

export default BoardEditor;
